import type { SiteOrder } from '@api/vendor-portal';

export interface OrderTotals {
    ordered: number;
    /** Passed QC and still at the site. */
    qcApproved: number;
    /** Declared, unruled, and still at the site. Either of these two may be dispatched. */
    qcPending: number;
    sent: number;
    /** How many orders the figures are drawn from, for the caption. */
    orderCount: number;
    /** Distinct SKUs across those orders — these are UNITS, and the two get confused. */
    skuCount: number;
    /**
     * What is still to MAKE: ordered minus what has been made and can still fill the order.
     *
     * MEASURED AT THE LINE, not at the gate. This used to be ordered-minus-sent, which
     * answered "what do I still owe" — a real question, but not the one a factory asks first.
     * A manufacturer plans the day around what is not made yet; what is made and not yet
     * collected is a different problem, and `toSend` below is where it now lives.
     *
     * `produced` arrives already net of anything rejected or expired, so a run that was a
     * fifth written off leaves those units here, still owed. Floored per line, so
     * over-production on one SKU cannot mask a shortfall on another.
     */
    openUnits: number;
    /**
     * What is still to SEND: ordered minus what has actually left.
     *
     * The old `openUnits`, under the name it always deserved. Between this and the two QC
     * figures a site can read its whole position: what is left to make, what is standing here
     * ready to go, and what the brand is still waiting for.
     */
    toSend: number;
}

/**
 * Column totals for the job cards below.
 *
 * DELIBERATELY THE SAME WORDS, IN THE SAME ORDER, as the columns on every line below. A summary
 * that invents its own vocabulary — "pending", "outstanding" — has to be explained and cannot be
 * checked; this one can be verified by adding up the column beneath it.
 *
 * FIVE OF THE SIX COLUMNS. `Manufactured` is deliberately absent: it is the one cumulative
 * figure on the row, and cumulative output summed across every open order answers a question
 * nobody asks at a glance. The other five all describe a position right now — what was asked
 * for, what is still owed, what is standing here in each QC state, and what has gone.
 *
 * OPEN ORDERS ONLY, which is now the caller's job to guarantee. It used to be free: the site's
 * own screen never fetched anything else. The brand's view of the same site fetches the whole
 * book, shipped and cancelled included, and handing that straight to this would quietly turn
 * "still to send" into a number counting work that went out weeks ago, or was called off —
 * `openOrdersOnly` below is what a caller with the wider list passes through first.
 *
 * These are not a partition and should not be read as one. Every unit is part of `ordered`
 * throughout; `qcApproved` and `qcPending` are disjoint from each other but both sit inside
 * `openUnits` until they leave, at which point they move to `sent`. Five separate questions
 * about the same commitment, not slices of it.
 */
/**
 * What is left of a commitment: what was asked for, minus what has been done about it.
 *
 * ONE FUNCTION, TWO QUESTIONS, because they are the same arithmetic and differ only in what
 * counts as "done". Pass `produced` and it answers what is still to make; pass `dispatched`
 * and it answers what is still to send. Naming it after either one — as `openQuantity` did —
 * is what made the second reading look like a different calculation.
 *
 * FLOORED AT ZERO PER LINE, so doing too much on one SKU cannot mask a shortfall on another:
 * an order 500 short here and 200 over there is 500 outstanding, not 300.
 *
 * Exported so the per-row columns and the summary above them cannot drift into two different
 * answers to the same question.
 */
export function remaining(asked: number, done: number): number {
    return Math.max(0, (asked || 0) - (done || 0));
}

export function buildOrderTotals(orders: SiteOrder[]): OrderTotals {
    const skus = new Set<string>();
    const totals = { ordered: 0, qcApproved: 0, qcPending: 0, sent: 0 };
    let openUnits = 0;
    let toSend = 0;

    for (const order of orders) {
        for (const line of order.lines ?? []) {
            skus.add(line.productId);
            totals.ordered += line.ordered ?? 0;
            totals.qcApproved += line.qcApproved ?? 0;
            totals.qcPending += line.qcPending ?? 0;
            totals.sent += line.dispatched ?? 0;
            // Both floored per line before summing, never after — see `remaining`.
            openUnits += remaining(line.ordered ?? 0, line.produced ?? 0);
            toSend += remaining(line.ordered ?? 0, line.dispatched ?? 0);
        }
    }

    return {
        ...totals,
        orderCount: orders.length,
        skuCount: skus.size,
        openUnits,
        toSend,
    };
}

/**
 * The subset of a site's book these totals may be built from.
 *
 * WHAT IS STILL LIVE, line by line rather than order by order, because one cancelled SKU on an
 * otherwise open order should leave the rest of it counted. An order left with no live lines
 * drops out entirely so the order count matches what a reader would tally by eye.
 *
 * Named rather than inlined at the call site so the rule sits next to the totals it protects:
 * the figures above are only true of open work, and this is what makes that so.
 */
const OPEN_LINE_STATUSES = new Set(['ISSUED', 'IN_PRODUCTION', 'READY_TO_SHIP']);

/**
 * `remaining`, for a line that may no longer be live.
 *
 * A cancelled line owes nothing, and neither does one closed short and marked done — yet
 * ordered-minus-made says both still do. The brand's views are fed the whole book, closed and
 * cancelled lines included, so the status has to be asked before the arithmetic is. Same set
 * as `openOrdersOnly` above, so a grid and the banner over it cannot disagree.
 */
export function openRemaining(status: string, asked: number, done: number): number {
    return OPEN_LINE_STATUSES.has(status) ? remaining(asked, done) : 0;
}

export function openOrdersOnly(orders: SiteOrder[]): SiteOrder[] {
    return orders
        .map((order) => ({
            ...order,
            lines: (order.lines ?? []).filter((line) => OPEN_LINE_STATUSES.has(line.status)),
        }))
        .filter((order) => order.lines.length > 0);
}
